import { useQuery } from '@tanstack/react-query';
import { createContext, type ReactNode, use, useMemo } from 'react';

import type { Conversation } from '../ddb/conversations';
import type { Session } from '../ddb/sessions';
import { currentUserQueryOptions } from '../serverFunctions/auth';

type AuthContextType = {
  /** Token-based auth: DynamoDB session record. */
  session?: Session;

  /** OIDC auth: Better Auth user id. */
  userId?: string;
  email?: string;
  familyName?: string;
  name?: string;

  conversationId?: string;
  currentConversation?: Conversation;
  events?: {
    id: string;
    title: string;
  }[];
  identityType?: 'token' | 'oidc';
  sessionId?: string;
  isLoading: boolean;
  refetch: () => void;
};

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const { data, refetch, isLoading } = useQuery(currentUserQueryOptions());

  const value = useMemo((): AuthContextType => {
    if (!data?.authenticated) {
      return { isLoading, refetch };
    }

    if (data.identityType === 'token') {
      return {
        identityType: 'token',
        session: data.session,
        conversationId: data.conversationId,
        currentConversation: data.currentConversation,
        sessionId: data.session.sessionId,
        events: data.events,
        isLoading,
        refetch,
      };
    }

    return {
      identityType: 'oidc',
      userId: data.userId,
      email: data.email,
      name: data.name,
      familyName: data.familyName,
      events: data.events,
      sessionId: data.sessionId,
      conversationId: data.conversationId,
      currentConversation: data.currentConversation,
      isLoading,
      refetch,
    };
  }, [isLoading, data, refetch]);

  return <AuthContext value={value}>{children}</AuthContext>;
};

export function useAuth() {
  const context = use(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
}
